import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import SettSkjema from "../components/dekkhotell/SettSkjema";
import KundeBanner from "../components/KundeBanner";
import { Tilbake, Sidetopp } from "../ny-kundesamhandling/Skjema";
import SesongSmsValg from "../components/dekkhotell/SesongSmsValg";
import { skrivUtEtikett } from "../utils/dekkhotellEtikett";
import {
  SETT_LABEL,
  LAGERSTED_LABEL,
  stedTekst,
  TILSTAND_LABEL,
  HENDELSE_LABEL,
  TOMT_SETT,
  kundeNavn,
  settMerkeCls,
  fmtDato,
} from "../constants/dekkhotell";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const motsatt = (s) => (s === "sommer" ? "vinter" : "sommer");

async function hentJson(url, opts) {
  const r = await fetch(url, opts);
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.message || "Noe gikk galt");
  return data;
}

function StedFelt({ lagersted, setLagersted, plassering, setPlassering, idPrefix }) {
  const felt = "w-full min-w-0 rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30";
  const etikett = "mb-1 block text-sm font-semibold text-slate-800";
  return (
    <>
      <div>
        <label htmlFor={`${idPrefix}-sted`} className={etikett}>Lagres hos</label>
        <select id={`${idPrefix}-sted`} value={lagersted} onChange={(e) => setLagersted(e.target.value)} className={felt}>
          {Object.entries(LAGERSTED_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor={`${idPrefix}-plass`} className={etikett}>Plassering (reol/hylle)</label>
        <input id={`${idPrefix}-plass`} value={plassering} onChange={(e) => setPlassering(e.target.value)} className={felt} />
      </div>
    </>
  );
}

function Flytt({ post, onFerdig, onAvbryt }) {
  const [lagersted, setLagersted] = useState(post.lagersted);
  const [plassering, setPlassering] = useState(post.plassering || "");
  const [feil, setFeil] = useState("");

  async function lagre(e) {
    e.preventDefault();
    setFeil("");
    try {
      onFerdig(
        await hentJson(`${API_BASE_URL}/dekkhotell/${post._id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ lagersted, plassering }),
        })
      );
    } catch (err) {
      setFeil(err.message);
    }
  }

  return (
    <form onSubmit={lagre} className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-3 space-y-3">
      <h2 className="text-lg font-bold text-slate-900">Flytt settet</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <StedFelt
          idPrefix="dh-flytt"
          lagersted={lagersted}
          setLagersted={setLagersted}
          plassering={plassering}
          setPlassering={setPlassering}
        />
      </div>
      {feil && <p role="alert" className="text-sm text-red-700">{feil}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className="inline-flex min-h-[2.5rem] items-center gap-2 rounded-xl bg-emerald-700 px-3 py-1.5 font-bold text-white hover:bg-emerald-800 disabled:opacity-60 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50">
          Lagre og skriv ut ny etikett
        </button>
        <button type="button" onClick={onAvbryt} className="px-4 py-2 rounded-lg border border-slate-300">Avbryt</button>
      </div>
    </form>
  );
}

function SettVisning({ tittel, s }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3">
      <h2 className="text-sm font-semibold text-slate-700 mb-2">{tittel}</h2>
      {s ? (
        <dl className="text-sm grid grid-cols-[auto,1fr] gap-x-3 gap-y-1">
          <dt className="text-slate-600">Sett</dt>
          <dd>
            <span className={`px-2 py-0.5 rounded-lg text-xs font-medium ${settMerkeCls(s.sett)}`}>
              {SETT_LABEL[s.sett]}
            </span>
            {s.pigger && <span className="ml-2 text-xs font-medium text-slate-700">Piggdekk</span>}
          </dd>
          <dt className="text-slate-600">Hva</dt>
          <dd>{s.antall} {s.form === "hjul" ? "hjul" : "dekk"}</dd>
          <dt className="text-slate-600">Dekk</dt>
          <dd>{s.beskrivelse || "—"}</dd>
          <dt className="text-slate-600">Tilstand</dt>
          <dd>{TILSTAND_LABEL[s.tilstand] || s.tilstand}</dd>
          {s.kommentar && (
            <>
              <dt className="text-slate-600">Kommentar</dt>
              <dd>{s.kommentar}</dd>
            </>
          )}
        </dl>
      ) : (
        <p className="text-sm text-slate-600">Ikke registrert.</p>
      )}
    </div>
  );
}

function Dekkskift({ post, onFerdig }) {
  const [inn, setInn] = useState(
    post.paSykkel ? { ...TOMT_SETT, ...post.paSykkel } : { ...TOMT_SETT, sett: motsatt(post.lagret.sett) }
  );
  const [lagersted, setLagersted] = useState(post.lagersted);
  const [plassering, setPlassering] = useState(post.plassering || "");
  const [serviceRef, setServiceRef] = useState("");
  const [betaltTil, setBetaltTil] = useState("");
  const [kommentar, setKommentar] = useState("");
  const [feil, setFeil] = useState("");
  const [lagrer, setLagrer] = useState(false);

  async function lagre(e) {
    e.preventDefault();
    setFeil("");
    setLagrer(true);
    try {
      const ny = await hentJson(`${API_BASE_URL}/dekkhotell/${post._id}/bytte`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          inn: { ...inn, sett: motsatt(post.lagret.sett) },
          lagersted,
          plassering,
          serviceRef,
          betaltTil: betaltTil || undefined,
          kommentar,
        }),
      });
      onFerdig(ny);
    } catch (err) {
      setFeil(err.message);
    } finally {
      setLagrer(false);
    }
  }

  const felt = "w-full min-w-0 rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30";
  const etikett = "mb-1 block text-sm font-semibold text-slate-800";
  return (
    <form onSubmit={lagre} className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-3 space-y-4">
      <h2 className="text-lg font-bold text-slate-900">Registrer dekkskift</h2>
      <p className="text-sm text-slate-700">
        {SETT_LABEL[post.lagret.sett]} monteres. Kontroller og beskriv settet som tas av og legges inn:
      </p>
      <SettSkjema idPrefix="dh-bytte" verdi={{ ...inn, sett: motsatt(post.lagret.sett) }} onChange={setInn} lasSett />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <StedFelt
          idPrefix="dh-bytte"
          lagersted={lagersted}
          setLagersted={setLagersted}
          plassering={plassering}
          setPlassering={setPlassering}
        />
        <div>
          <label htmlFor="dh-bytte-ref" className={etikett}>Verkstedordre (betaling)</label>
          <input id="dh-bytte-ref" placeholder="RSS-…" value={serviceRef} onChange={(e) => setServiceRef(e.target.value)} className={felt} />
        </div>
        <div>
          <label htmlFor="dh-bytte-betalt" className={etikett}>Lagring betalt til</label>
          <input id="dh-bytte-betalt" type="date" value={betaltTil} onChange={(e) => setBetaltTil(e.target.value)} className={felt} />
        </div>
        <div className="md:col-span-2 lg:col-span-4">
          <label htmlFor="dh-bytte-komm" className={etikett}>Kommentar</label>
          <input id="dh-bytte-komm" value={kommentar} onChange={(e) => setKommentar(e.target.value)} className={felt} />
        </div>
      </div>
      {feil && <p role="alert" className="text-sm text-red-700">{feil}</p>}
      <button
        type="submit"
        disabled={lagrer}
        className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-60"
      >
        {lagrer ? "Lagrer…" : "Lagre dekkskift og skriv ut etikett"}
      </button>
    </form>
  );
}

export default function DekkhotellDetalj() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [feil, setFeil] = useState("");
  const [visBytte, setVisBytte] = useState(false);
  const [visFlytt, setVisFlytt] = useState(false);
  const [notat, setNotat] = useState("");
  const [sesongSms, setSesongSms] = useState(null);

  const hent = useCallback(async () => {
    try {
      const p = await hentJson(`${API_BASE_URL}/dekkhotell/${id}`);
      setPost(p);
      setNotat(p.notat || "");
      if (p.kunde?._id) {
        const svar = await hentJson(`${API_BASE_URL}/dekkhotell/sesong-sms/${p.customerId._id}`);
        setSesongSms(svar.verdi);
      }
    } catch (e) {
      setFeil(e.message);
    }
  }, [id]);

  // Gjelder kunden, ikke bare denne posten.
  async function endreSesongSms(verdi) {
    const forrige = sesongSms;
    setSesongSms(verdi);
    try {
      await hentJson(`${API_BASE_URL}/dekkhotell/sesong-sms/${post.kunde._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ verdi }),
      });
    } catch (e) {
      setSesongSms(forrige);
      alert(e.message);
    }
  }

  useEffect(() => {
    hent();
  }, [hent]);

  async function patch(felter) {
    try {
      const p = await hentJson(`${API_BASE_URL}/dekkhotell/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(felter),
      });
      setPost(p);
    } catch (e) {
      alert(e.message);
    }
  }

  async function utlever() {
    if (!window.confirm(`Kunden tar med seg ${SETT_LABEL[post.lagret.sett].toLowerCase()} og avslutter dekkhotellet?`)) return;
    try {
      setPost(await hentJson(`${API_BASE_URL}/dekkhotell/${id}/utlever`, { method: "POST" }));
    } catch (e) {
      alert(e.message);
    }
  }

  async function slett() {
    if (!window.confirm("Slette feilregistreringen?")) return;
    try {
      await hentJson(`${API_BASE_URL}/dekkhotell/${id}`, { method: "DELETE" });
      navigate("/dekkhotell");
    } catch (e) {
      alert(e.message);
    }
  }

  if (feil) return <p role="alert" className="p-4 text-red-700">{feil}</p>;
  if (!post) return <p className="p-4 text-slate-600">Laster …</p>;

  const aktiv = post.status === "lagret";
  const knapp = "inline-flex min-h-[2.25rem] items-center gap-1.5 rounded-lg border-2 border-slate-300 bg-white px-3 py-1 text-sm font-semibold text-slate-800 hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50";

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-10">
      <Tilbake til="/dekkhotell">Tilbake til dekkhotellet</Tilbake>
      <Sidetopp
        ikon="dekk"
        farge="lager"
        tittel={<span className="flex flex-wrap items-center gap-2">{post.nr}
          <span className={`rounded-full px-2.5 py-0.5 text-sm font-semibold ${aktiv ? "bg-emerald-100 text-emerald-900" : "bg-slate-200 text-slate-700"}`}>
            {aktiv ? "På lager" : `Utlevert ${fmtDato(post.avsluttetDato)}`}
          </span></span>}
        undertittel="Dekkhotell"
      />
      <KundeBanner
        kundeId={post.kunde?._id}
        navn={post.kunde ? kundeNavn(post.kunde) : "Ukjent kunde"}
        telefon={post.kunde?.phoneNumber}
        epost={post.kunde?.email}
      >
          <p className="text-sm text-slate-700">
            {post.bikeId ? (
              <Link to={`/sykkelkort/${post.bikeId._id}`} className="font-semibold text-indigo-700 underline underline-offset-2">
                {[post.bikeId.merke, post.bikeId.modell].filter(Boolean).join(" ")} ({post.bikeId.shortId})
              </Link>
            ) : (
              post.sykkel || "Sykkel ikke registrert"
            )}
            {post.bikeId?.elsykkel && " · elsykkel"}
          </p>
      </KundeBanner>

      <div className="flex flex-wrap gap-2">
        {aktiv && (
          <button type="button" onClick={() => setVisBytte((v) => !v)} className="inline-flex min-h-[2.25rem] items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-1 text-sm font-semibold text-white hover:bg-emerald-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50" aria-expanded={visBytte}>
            Dekkskift
          </button>
        )}
        <button type="button" onClick={() => skrivUtEtikett(post)} className={knapp}>Skriv ut etikett</button>
        {aktiv && (
          <button type="button" onClick={() => setVisFlytt((v) => !v)} className={knapp} aria-expanded={visFlytt}>
            Flytt
          </button>
        )}
        {aktiv && <button type="button" onClick={utlever} className={knapp}>Utlever og avslutt</button>}
        {!post.hendelser?.some((h) => h.type === "bytte") && (
          <button type="button" onClick={slett} className={`${knapp} text-red-700`}>Slett feilregistrering</button>
        )}
      </div>

      {visFlytt && aktiv && (
        <Flytt
          post={post}
          onAvbryt={() => setVisFlytt(false)}
          onFerdig={(ny) => {
            setPost(ny);
            setVisFlytt(false);
            skrivUtEtikett(ny);
          }}
        />
      )}

      {visBytte && aktiv && (
        <Dekkskift
          post={post}
          onFerdig={(ny) => {
            setPost(ny);
            setVisBytte(false);
            skrivUtEtikett(ny);
          }}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SettVisning tittel={aktiv ? `På lager · ${stedTekst(post) || "plass ikke satt"}` : "Sist lagret"} s={post.lagret} />
        <SettVisning tittel="På sykkelen" s={post.paSykkel} />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div>
          <span className="text-slate-600 block">Lagring betalt til</span>
          <input
            type="date"
            aria-label="Lagring betalt til"
            value={post.betaltTil ? String(post.betaltTil).slice(0, 10) : ""}
            onChange={(e) => patch({ betaltTil: e.target.value || null })}
            className="p-1 border border-slate-300 rounded-lg"
          />
        </div>
        <div>
          <span className="text-slate-600 block">Siste påminnelse</span>
          {post.varsel?.sisteDato ? `${fmtDato(post.varsel.sisteDato)} (${SETT_LABEL[post.varsel.sisteSett]})` : "—"}
        </div>
        <div>
          {post.kunde?._id && (
            <SesongSmsValg idPrefix="dh-detalj" verdi={sesongSms} onChange={endreSesongSms} visSporsmal={false} />
          )}
        </div>
        <div className="md:col-span-3">
          <label htmlFor="dh-notat" className="text-slate-600 block">Notat</label>
          <textarea
            id="dh-notat"
            rows={2}
            value={notat}
            onChange={(e) => setNotat(e.target.value)}
            onBlur={() => notat !== (post.notat || "") && patch({ notat })}
            className="w-full p-2 border border-slate-300 rounded-lg"
          />
        </div>
      </div>

      <section>
        <h2 className="text-lg font-bold text-slate-900 mb-2">Historikk</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-slate-200 rounded-xl text-sm">
            <thead>
              <tr className="bg-slate-100 text-left text-slate-700">
                <th className="px-3 py-2">Dato</th>
                <th className="px-3 py-2">Hva</th>
                <th className="px-3 py-2">Sett</th>
                <th className="px-3 py-2">Sted</th>
                <th className="px-3 py-2">Ordre</th>
                <th className="px-3 py-2">Av</th>
                <th className="px-3 py-2">Kommentar</th>
              </tr>
            </thead>
            <tbody>
              {[...(post.hendelser || [])].reverse().map((h, i) => (
                <tr key={i} className="border-t">
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDato(h.dato)}</td>
                  <td className="px-3 py-2">{HENDELSE_LABEL[h.type] || h.type}</td>
                  <td className="px-3 py-2">{SETT_LABEL[h.sett] || ""}</td>
                  <td className="px-3 py-2">{stedTekst(h)}</td>
                  <td className="px-3 py-2">{h.serviceRef}</td>
                  <td className="px-3 py-2">{h.av}</td>
                  <td className="px-3 py-2">{h.kommentar}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
